import { isPromise } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { hooks } from '@wove/react';
import { CSSProperties, Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { dynamicNode } from '../dynamic-node';
import { Progress } from '../progress';
import { Space } from '../space';
import { CommonPropsWithChildren } from '../utils/native-props';
import './style.less';

/**
 * Button内置Var Style
 * ```
 *  1. --button-color button主色系
 *  2. --button-full-solid-bg-color button在solid填充模式下的背景色，默认值：--button-color
 *  3. --button-full-solid-text-color button在solid填充模式下的文字颜色，默认值：#fff
 *  4. --button-full-solid-loading-color button在solid填充模式下loading颜色，默认值：solid模式默认为白色，其他模式下为--button-color
 *  5. --button-full-solid-box-shadow button在solid填充模式下阴影设置，无默认值
 *
 *  6. --button-full-outline-bg-color button在outline填充模式下背景色，默认值：#fff
 *  7. --button-full-outline-box-shadow button在outline填充模式下阴影设置，无默认值
 *
 *  8. --button-border-radius button button边框圆角数值，默认值：10px
 *  9. --button-border-color button边框颜色，solid、outline模式下有效果
 * ```
 */
export type ButtonVarStyle = {
  '--button-color': CSSProperties['color'];
  '--button-font-size': CSSProperties['fontSize'];
  '--button-padding': CSSProperties['padding'];
  '--button-full-solid-bg-color': CSSProperties['backgroundColor'];
  '--button-full-solid-text-color': CSSProperties['color'];
  '--button-full-solid-loading-color': CSSProperties['color'];
  '--button-full-solid-box-shadow': CSSProperties['boxShadow'];
  '--button-full-outline-bg-color': CSSProperties['backgroundColor'];
  '--button-full-outline-box-shadow': CSSProperties['boxShadow'];
  '--button-border-radius': CSSProperties['borderRadius'];
  '--button-border-color': CSSProperties['borderColor'];
};

export type ButtonProps = CommonPropsWithChildren<ButtonVarStyle> & {
  type?: 'default' | 'primary' | 'secondary' | 'danger';
  // 填充模式
  fill?: 'solid' | 'outline' | 'none';
  size?: 'mini' | 'small' | 'middle' | 'large';
  onClick?: (e) => void | Promise<void>;
  disabled?: boolean;
  inline?: boolean;
  ellipsis?: boolean;
  beforeIcon?: ReactNode;
  afterIcon?: ReactNode;
  stopPropagation?: boolean;
  loading?: boolean;
  /** loading全局蒙层，默认值：false */
  loadingMask?: boolean;
  // 重复点击间隙，单位毫秒 默认值：500
  debounceDuration?: number;
};

const ButtonContent = (buttonProps: ButtonProps) => {
  if (buttonProps.beforeIcon || buttonProps.afterIcon || buttonProps.loading) {
    const progressSize = {
      mini: 20,
      small: 24,
      middle: 26,
      large: 30,
    };
    return (
      <Space justifyContent="center" gap={10}>
        {buttonProps.loading && (
          <Fragment>
            {<Progress value={0.2} gap={3} size={progressSize[buttonProps.size || 'middle']} />}
          </Fragment>
        )}
        {buttonProps.beforeIcon && <Fragment>{buttonProps.beforeIcon}</Fragment>}
        <span className="v-button-text">{buttonProps.children}</span>
        {buttonProps.afterIcon && <Fragment>{buttonProps.afterIcon}</Fragment>}
      </Space>
    );
  }
  return <Fragment>{buttonProps.children}</Fragment>;
};

export const Button = (props: ButtonProps) => {
  const lockRef = useRef(false);
  const {
    children,
    disabled,
    loading,
    type = 'default',
    inline = false,
    fill = 'solid',
    ellipsis = true,
    size = 'middle',
    debounceDuration = 500,
  } = props;
  const [innerLoading, setInnerLoading] = useState(false);

  const loadingRst = innerLoading || loading;
  const className = classNames(
    'v-button',
    `v-button-${type}`,
    {
      'v-button-loading': loadingRst,
      'v-button-inline': inline,
      'v-button-ellipsis': ellipsis,
    },
    `v-button-full-${fill}`,
    `v-button-${size}`,
    props.className,
  );

  const handleClick = hooks.useDebounceClick((e) => {
    if (props.stopPropagation) {
      e.stopPropagation();
    }
    if (lockRef.current) return;
    const onClick = props.onClick;
    if (!onClick) {
      return;
    }
    const result = onClick(e);
    if (result && isPromise(result)) {
      lockRef.current = true;
      setInnerLoading(true);
      result
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          lockRef.current = false;
          setInnerLoading(false);
        });
    }
  }, debounceDuration);

  useEffect(() => {
    if (props.loadingMask) {
      if (loadingRst) {
        dynamicNode.append({ fixed: true, className: 'v-button-loading-mask' });
      } else {
        dynamicNode.remove();
      }
    }
  }, [loadingRst, props.loadingMask]);

  return (
    <Fragment>
      <div className="v-button-full-spinner"></div>
      <button
        type="button"
        className={className}
        disabled={disabled || loadingRst}
        style={props.style}
        onClick={handleClick}
      >
        <ButtonContent {...props} loading={loadingRst}>
          {children}
        </ButtonContent>
      </button>
    </Fragment>
  );
};
